/* G3D custom (2026-10-10): Legendary Trio Bundle page (sections/legendary-trio-bundle.liquid), the Starter Trio's
   direction C "card fan" with legendary data (user's pick). Follows assets/starter-trio.js; ported from the approved mockup
   (Obsidian vault: Website/mockups/legendary-trio-fan.html, after phase 2). Variants, prices, stock and photos come from
   the #lt-data block the section renders. Add to cart fires the theme's CartLinesUpdateEvent, like
   sections/build-your-bundle.liquid, so the header bubble and the cart drawer refresh. */
import { addToCart } from '@theme/g3d-cart';
import { lockScroll, unlockScroll } from '@theme/utilities';

const root = document.querySelector('.g3d-lt');
const $ = id => document.getElementById(id);
const DATA = JSON.parse($('lt-data').textContent);
const size = (url, w) => url.replace(/width=\d+/, 'width=' + w); // the section hands out 1000px image URLs
const GROUPS = {};
DATA.groups.forEach(([trio, shiny, src]) => { (GROUPS[trio] = GROUPS[trio] || [])[shiny] = src; });
// dark text or white, whichever reads better on the legend colour (WCAG luminance; 0.197 is where #16161a and white tie)
const onColour = hex => {
  const [r, g, b] = [1, 3, 5].map(i => { const c = parseInt(hex.slice(i, i + 2), 16) / 255; return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.197 ? '#16161a' : '#fff';
};
// [trio, short name, region, then 3 legends as [name, element, national dex no., colour]]. The middle legend is the one the
// trio art raises, as on the bundles page. A trio shows only if the product has both its "<trio> (<region>)" variants
// (never a Normal one standing in for a Shiny order).
const TRIOS = [
  ['Legendary Birds', 'Birds', 'Kanto', ['Articuno', 'Ice', 144, '#6fc2ef'], ['Zapdos', 'Lightning', 145, '#f3cd1b'], ['Moltres', 'Fire', 146, '#f2782a']],
  ['Legendary Beasts', 'Beasts', 'Johto', ['Raikou', 'Thunder', 243, '#f0bf2c'], ['Entei', 'Fire', 244, '#b4562e'], ['Suicune', 'Water', 245, '#46b9d6']],
  ['Regi Trio', 'Regi', 'Hoenn', ['Regirock', 'Rock', 377, '#b9773f'], ['Regice', 'Ice', 378, '#8fd3ee'], ['Registeel', 'Steel', 379, '#8d93a3']],
  ['Weather Trio', 'Weather', 'Hoenn', ['Kyogre', 'Sea', 382, '#2b6fd8'], ['Rayquaza', 'Sky', 384, '#2f9e5a'], ['Groudon', 'Land', 383, '#d3402b']],
  ['Lake Trio', 'Lake', 'Sinnoh', ['Uxie', 'Knowledge', 480, '#e9c44c'], ['Azelf', 'Willpower', 482, '#3d86d4'], ['Mesprit', 'Emotion', 481, '#e985b0']],
  ['Creation Trio', 'Creation', 'Sinnoh', ['Dialga', 'Time', 483, '#4a6fc4'], ['Giratina', 'Distortion', 487, '#6a5590'], ['Palkia', 'Space', 484, '#d58fca']],
  ['Tao Trio', 'Tao', 'Unova', ['Reshiram', 'Truth', 643, '#e6e6ec'], ['Zekrom', 'Ideals', 644, '#2e2e3a'], ['Kyurem', 'Ice', 646, '#8fb5c9']],
  ['Aura Trio', 'Aura', 'Kalos', ['Xerneas', 'Life', 716, '#3a74cb'], ['Yveltal', 'Destruction', 717, '#c1243c'], ['Zygarde', 'Order', 718, '#3ba45b']],
  ['Light Trio', 'Light', 'Alola', ['Solgaleo', 'Sun', 791, '#f2a43a'], ['Necrozma', 'Light', 800, '#3a3a4c'], ['Lunala', 'Moon', 792, '#6c4cc5']]
].map(([name, short, region, ...m]) => {
  const option = `${name} (${region})`;
  return { name, short, region, label: `${name} · ${region}`, group: GROUPS[name] || [],
    // a legend's photos come from its "<Name> Ball" product: [front, shiny front]; none = not printed yet ("photo soon")
    legends: m.map(([name, el, dex, c]) => ({ name, el, dex, c, on: onColour(c), file: DATA.photos[name]?.[0], shinyFile: DATA.photos[name]?.[1] })),
    variants: Object.fromEntries(['Normal', 'Shiny'].map(st => [st, DATA.variants.find(v => v.trio === option && v.style === st)])) };
}).filter(t => t.variants.Normal && t.variants.Shiny);
console.assert(TRIOS.length === 9, 'a trio is missing its Normal or Shiny variant in the store, so it is hidden');

const dexNo = m => 'No.' + String(m.dex).padStart(3, '0');
const shiny = () => state.style === 'Shiny';
// the shiny photo when we have one, else the normal one
const photo = m => (shiny() && m.shinyFile) || m.file;
// a legend's ball photo, or the "photo soon" tile
const tile = m => m.file ? `<img src="${size(photo(m), 500)}" alt="${shiny() && m.shinyFile ? 'Shiny ' : ''}${m.name} Ball">`
  : `<span class="lt-soon" style="--c:${m.c};--on:${m.on}" role="img" aria-label="${m.name} Ball, photo coming soon"><i></i><b>${m.name}</b><small>Photo soon</small></span>`;
const replay = (el, cls) => { el.classList.remove(cls); void el.offsetWidth; el.classList.add(cls); };
const and = a => a.length > 1 ? a.slice(0, -1).join(', ') + ' and ' + a.at(-1) : a[0];
const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
const stage = root.querySelector('.lt-stage'), fan = $('lt-fan'), strip = $('lt-strip'), atc = $('lt-atc');

// spans, not divs: everything inside a button has to be phrasing content
fan.innerHTML = [0, 1, 2].map(i => `
  <button type="button" class="lt-card" data-t="${i}" style="--i:${i}">
    <span class="lt-card__in">
      <span class="lt-face lt-front">
        <i class="lt-foil"></i>
        <span class="lt-card__top"><b></b><span></span></span>
        <span class="lt-ph lt-card__ph"></span>
        <span class="lt-card__foot"><span class="lt-type"></span></span>
      </span>
      <span class="lt-face lt-back" aria-hidden="true"><b>G3D</b></span>
    </span>
  </button>`).join('');
const cards = [...fan.querySelectorAll('.lt-card')];

strip.innerHTML = TRIOS.map((t, i) => `<button type="button" class="lt-gen" role="radio" aria-label="${t.label}" data-gen="${i}">
  <span class="lt-mini" aria-hidden="true">${t.legends.map(m => `<span style="--c:${m.c}">${m.file ? `<img src="${size(m.file, 80)}" alt="">` : ''}</span>`).join('')}</span>
  <b>${t.short}</b><small>${t.region}</small></button>`).join('') + '<span class="lt-pball" aria-hidden="true"></span>';
const gens = [...strip.querySelectorAll('.lt-gen')], marker = strip.querySelector('.lt-pball');
$('lt-gens').insertAdjacentHTML('beforeend', TRIOS.map((t, i) =>
  `<label class="lt-opt"><input type="radio" name="lt-gen" value="${i}"><span><b>${t.short}</b><small>${t.region}</small></span></label>`).join(''));

/* ---- state: the buy box, the cards and the strip all read it; any of them can change it ---- */
const current = DATA.variants.find(v => v.id === DATA.current) || DATA.variants[0];
const state = { gen: Math.max(0, TRIOS.findIndex(t => `${t.name} (${t.region})` === current.trio)), style: current.style === 'Shiny' ? 'Shiny' : 'Normal' };
const variant = () => TRIOS[state.gen].variants[state.style];
let addedTimer, busy = false, timers = [];

function render() {
  const t = TRIOS[state.gen], on = gens[state.gen], sh = shiny(), v = variant(), printed = t.legends.filter(m => m.file);
  root.querySelectorAll('.lt-opt input').forEach(inp => inp.checked = inp.value === String(inp.name === 'lt-gen' ? state.gen : state.style));
  $('lt-genName').textContent = '· ' + t.label;
  // the price sits in a live region: rewriting the same text would announce it again
  if ($('lt-price').textContent !== v.money) $('lt-price').textContent = v.money;
  if ($('lt-save').textContent !== 'Save ' + v.save) $('lt-save').textContent = $('lt-tagSave').textContent = 'Save ' + v.save;
  $('lt-save').hidden = $('lt-tagSave').hidden = !v.save;
  $('lt-boxIntro').textContent = `3 ${sh ? 'shiny ' : ''}balls: the ${t.name} of ${t.region}, one of each legend:`;
  $('lt-boxList').innerHTML = t.legends.map(m => `<li style="--c:${m.c}">${m.name}<small>${m.el}</small></li>`).join('');
  $('lt-boxFig').hidden = !t.group[0];
  if (t.group[0]) {
    const shinyGroup = sh && t.group[1];
    $('lt-boxImg').src = size(shinyGroup || t.group[0], 800);
    $('lt-boxImg').alt = `The ${t.name}${shinyGroup ? ' in shiny colours' : ''}: ${and(t.legends.map(m => m.name))}`;
    $('lt-boxCap').textContent = `Real photo of the ${t.name}${shinyGroup ? ', shiny prints' : sh ? ' (normal colours)' : ''}`;
  }
  const todo = t.legends.filter(m => !m.file).map(m => m.name);
  $('lt-photoNote').textContent = !todo.length ? '' : printed.length
    ? `${and(todo)} ${todo.length > 1 ? 'are' : 'is'} still waiting for a first print run, so ${todo.length > 1 ? 'they show' : 'it shows'} a "photo soon" tile. Orderable now either way.`
    : `This trio hasn't had its first print run yet, so there are no photos. It's fully orderable now; photos follow the first print.`;
  $('lt-photoNote').hidden = !todo.length;
  atc.disabled = !v.available;
  atc.textContent = !v.available ? 'Sold out' : busy ? 'Adding…' : atc.classList.contains('lt-is-added') ? 'Added to cart ✓'
    : atc.classList.contains('lt-is-error') ? "Couldn't add it. Try again" : 'Add to cart · ' + v.money;

  $('lt-genTag').textContent = t.label;
  gens.forEach((b, i) => { b.setAttribute('aria-checked', i === state.gen); b.tabIndex = i === state.gen ? 0 : -1; });
  marker.style.transform = `translateX(${on.offsetLeft + on.offsetWidth / 2 - 9}px)`;
  stage.classList.toggle('lt-is-shiny', sh);
  const shinyShots = printed.filter(m => m.shinyFile).length;
  $('lt-note').innerHTML = sh ? '<i class="lt-star">✦</i> ' + (!printed.length ? 'Shiny prints come in their shiny colours.'
      : shinyShots === printed.length ? 'Showing the shiny prints.'
      : shinyShots ? 'Showing shiny photos where we have them; the others show the normal print for now.'
      : 'Shiny prints come in their shiny colours. The photos show the normal print for now.')
    // which balls still wait for photos is said once, in the box below
    : printed.length === 3 ? 'Open a card to see its photo up close, or deal another trio below.'
    : printed.length ? 'Open a card with a photo to see it up close, or deal another trio below.'
    : 'Deal another trio below.';
}

// keep the chosen trio in view inside the strip without scrolling the page; only on a real change, so it never pulls back a swipe
const centre = () => { const on = gens[state.gen]; strip.scrollTo({ left: on.offsetLeft - (strip.clientWidth - on.offsetWidth) / 2, behavior: REDUCED ? 'auto' : 'smooth' }); };

function fillCard(card, i) {
  const t = TRIOS[state.gen], m = t.legends[i];
  card.style.setProperty('--c', m.c);
  card.style.setProperty('--on', m.on);
  card.querySelector('.lt-card__top b').textContent = m.name;
  card.querySelector('.lt-card__top span').textContent = dexNo(m);
  card.querySelector('.lt-ph').innerHTML = tile(m);
  card.querySelector('.lt-type').textContent = m.el;
  // a "photo soon" card has nothing to zoom into
  card.disabled = !m.file;
  card.setAttribute('aria-label', `${m.name} Ball, ${m.el}, ${m.file ? 'view the photo up close' : 'photo coming soon'}`);
}

function set(patch) {
  patch = Object.fromEntries(Object.entries(patch).filter(([k, v]) => state[k] !== v));
  if (!Object.keys(patch).length) return;
  Object.assign(state, patch);
  clearTimeout(addedTimer);
  atc.classList.remove('lt-is-added', 'lt-is-error');
  render();
  // keep ?variant= in step, like the theme's own picker, so shared links open this trio and style
  const u = new URL(location.href);
  u.searchParams.set('variant', variant().id);
  history.replaceState(history.state, '', u);
  // Shiny swaps photos in place (some legends have shiny shots); a new trio deals the cards again
  if (!('gen' in patch)) return cards.forEach(fillCard);
  centre();
  // deal: flip all three; each front is refilled while its back faces the viewer
  timers.forEach(clearTimeout);
  if (REDUCED) return cards.forEach(fillCard);
  timers = cards.map((c, i) => { replay(c, 'lt-is-flip'); return setTimeout(() => fillCard(c, i), 400 + i * 90); });
}
root.addEventListener('change', e => {
  if (e.target.name === 'lt-gen') set({ gen: +e.target.value });
  if (e.target.name === 'lt-style') set({ style: e.target.value });
});
strip.onclick = e => { const b = e.target.closest('.lt-gen'); if (b) set({ gen: +b.dataset.gen }); };
strip.onkeydown = e => { // radiogroup arrow keys
  const d = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 }[e.key];
  if (!d) return;
  e.preventDefault();
  set({ gen: (state.gen + d + TRIOS.length) % TRIOS.length });
  gens[state.gen].focus();
};
// only a real width change re-deals: iOS Safari fires resize whenever its toolbar hides or shows while scrolling
let lastW = innerWidth;
addEventListener('resize', () => { if (innerWidth !== lastW) { lastW = innerWidth; render(); centre(); } });

/* ---- add to cart: the same event the theme's own add-to-cart fires, so the header bubble and the cart drawer refresh ---- */
function flash(cls, message) {
  busy = false;
  $('lt-status').textContent = message;
  atc.classList.add(cls);
  render();
  clearTimeout(addedTimer);
  addedTimer = setTimeout(() => { atc.classList.remove(cls); render(); }, cls === 'lt-is-added' ? 2000 : 2600);
}
atc.onclick = e => {
  e.preventDefault(); // the form posts only when this script never ran
  const v = variant(), t = TRIOS[state.gen], style = state.style;
  if (busy || !v.available) return;
  // the header announces the cart count from the same event, so this message leaves it out
  const msg = `Legendary Trio Bundle, ${t.name}, ${style}, added to your cart.`;
  // "Added ✓" only on the choice that was added; the shopper may have picked another one meanwhile
  const added = () => { if (variant() === v) flash('lt-is-added', msg); else { busy = false; $('lt-status').textContent = msg; render(); } };
  busy = true;
  render();
  addToCart(atc, [{ id: v.id, quantity: 1 }], 'legendary-trio-bundle') // a failed cart refresh still counts as added: a retry would add a second one
    .then(added, err => flash('lt-is-error', "Couldn't add the bundle to your cart: " + err.message))
    .finally(() => { busy = false; });
};

/* ---- photo viewer: steps through this trio's photographed legends ---- */
const lb = $('lt-lb'), box = lb.firstElementChild, lbImg = $('lt-lbImg');
let list = [], at = 0, opener;
function show(i) {
  at = (i + list.length) % list.length;
  const m = list[at], sh = shiny(), real = sh && m.shinyFile, src = size(photo(m), 1000);
  if (lbImg.src !== src) { lbImg.classList.add('lt-is-loading'); lbImg.src = src; }
  // a shiny order without a shiny photo says so instead of pretending
  lbImg.alt = (real ? 'Shiny ' : '') + m.name + ' Ball' + (sh && !real ? ', normal colours shown' : '');
  $('lt-lbName').textContent = m.name;
  $('lt-lbNo').textContent = dexNo(m);
  $('lt-lbType').textContent = m.el;
  $('lt-lbType').style.cssText = `--c:${m.c};--on:${m.on}`;
  $('lt-lbPos').textContent = list.length > 1 ? `${at + 1} / ${list.length}` : '';
  $('lt-lbSub').textContent = TRIOS[state.gen].label + (sh && !real ? ' · Shiny print, shown here in normal colours until its photo is ready' : '');
  $('lt-lbPrev').hidden = $('lt-lbNext').hidden = list.length < 2;
  lb.classList.toggle('lt-is-shiny', sh);
  box.style.setProperty('--c', sh ? 'var(--gold)' : m.c);
}
lbImg.onload = lbImg.onerror = () => lbImg.classList.remove('lt-is-loading');
fan.onclick = e => {
  const card = e.target.closest('.lt-card');
  if (!card || card.disabled) return;
  const t = TRIOS[state.gen];
  opener = card;
  list = t.legends.filter(m => m.file);
  // mid-deal a card can still hold the last trio's legend, which may have no photo in this one
  const i = list.indexOf(t.legends[+card.dataset.t]);
  if (i < 0) return;
  show(i);
  lb.showModal();
  lockScroll(lb);
  // grow out of the card that was clicked
  const c = card.getBoundingClientRect(), b = box.getBoundingClientRect();
  box.style.transformOrigin = `${c.left + c.width / 2 - b.left}px ${c.top + c.height / 2 - b.top}px`;
  replay(box, 'lt-is-in');
};
$('lt-lbPrev').onclick = () => show(at - 1);
$('lt-lbNext').onclick = () => show(at + 1);
$('lt-lbClose').onclick = () => lb.close();
lb.onclick = e => { if (e.target === lb) lb.close(); }; // a click on the backdrop lands on the dialog itself
// Escape belongs to this dialog: a docked cart drawer listens on document and would close instead
lb.onkeydown = e => { if (e.key === 'Escape') e.stopPropagation(); if (e.key === 'ArrowLeft') show(at - 1); if (e.key === 'ArrowRight') show(at + 1); };
lb.onclose = () => { unlockScroll(lb); opener?.focus(); };

// loops pause while the gallery is off screen
new IntersectionObserver(([e]) => stage.classList.toggle('lt-is-paused', !e.isIntersecting)).observe(stage);
// Pause motion holds the Shiny shine loops (the viewer's too) until pressed again; the label carries the state
const motion = $('lt-motion');
motion.onclick = () => {
  motion.style.minWidth = motion.getBoundingClientRect().width + 'px'; // keeps the longer "Pause motion" width, so the chip doesn't jump
  motion.textContent = root.classList.toggle('lt-is-still') ? 'Play motion' : 'Pause motion';
};
cards.forEach(fillCard);
render();
centre();
root.classList.add('lt-ready'); // the section's fallback script checks this on load
